/**
 * DST-aware timezone utilities.
 *
 * Everything the chart draws is computed from absolute epoch seconds; the
 * supplied IANA timezone is used to decide where local midnights fall, how
 * long a day is (23/24/25 h), where hour ticks are, and how weeks/dates are
 * labelled. No dependency on the host's timezone.
 */
const SECOND = 1000;
const DAY_MS = 86400000;
const formatterCache = new Map();
const CACHE_LIMIT = 8000;
const offsetCache = new Map();
const clockCache = new Map();
const dayStartCache = new Map();
const nextDayCache = new Map();
const ticksCache = new Map();
const dstCache = new Map();
function partsFormatter(timeZone) {
    let formatter = formatterCache.get(timeZone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat("en-US", {
            timeZone,
            hourCycle: "h23",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
        formatterCache.set(timeZone, formatter);
    }
    return formatter;
}
/** Local wall-clock parts of an instant in `timeZone`. */
export function zonedParts(epochSeconds, timeZone) {
    const parts = partsFormatter(timeZone).formatToParts(new Date(epochSeconds * SECOND));
    let year = 0;
    let month = 0;
    let day = 0;
    let hour = 0;
    let minute = 0;
    let second = 0;
    for (const part of parts) {
        switch (part.type) {
            case "year":
                year = Number(part.value);
                break;
            case "month":
                month = Number(part.value);
                break;
            case "day":
                day = Number(part.value);
                break;
            case "hour":
                hour = Number(part.value) % 24;
                break;
            case "minute":
                minute = Number(part.value);
                break;
            case "second":
                second = Number(part.value);
                break;
            default:
                break;
        }
    }
    return { year, month, day, hour, minute, second };
}
/** Offset of `timeZone` at an instant, in milliseconds (east positive). */
export function offsetMsAt(epochSeconds, timeZone) {
    const key = `${timeZone}|${epochSeconds}`;
    const cached = offsetCache.get(key);
    if (cached !== undefined)
        return cached;
    const parts = zonedParts(epochSeconds, timeZone);
    const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    const value = asUtc - Math.floor(epochSeconds) * SECOND;
    if (offsetCache.size > CACHE_LIMIT)
        offsetCache.clear();
    offsetCache.set(key, value);
    return value;
}
/**
 * Wall-clock time in `timeZone` -> absolute epoch seconds.
 *
 * Ambiguous times (clocks go back) resolve to the earlier occurrence.
 * Nonexistent times (clocks spring forward) shift forward past the gap.
 */
export function zonedEpoch(year, month, day, hour, minute, timeZone) {
    const utcGuess = Date.UTC(year, month - 1, day, hour, minute, 0);
    const offsets = new Set();
    for (const probe of [utcGuess - 2 * DAY_MS, utcGuess, utcGuess + 2 * DAY_MS]) {
        offsets.add(offsetMsAt(probe / SECOND, timeZone));
    }
    const candidates = [];
    for (const offset of offsets) {
        const candidate = utcGuess - offset;
        const parts = zonedParts(candidate / SECOND, timeZone);
        if (parts.year === year &&
            parts.month === month &&
            parts.day === day &&
            parts.hour === hour &&
            parts.minute === minute) {
            candidates.push(candidate);
        }
    }
    if (candidates.length > 0) {
        return Math.round(Math.min(...candidates) / SECOND);
    }
    // Nonexistent wall-clock time (spring-forward gap): move forward past it.
    let best = utcGuess - offsetMsAt(utcGuess / SECOND, timeZone);
    for (const offset of offsets) {
        best = Math.max(best, utcGuess - offset);
    }
    return Math.round(best / SECOND);
}
/** Epoch seconds of the local midnight that starts the day containing `epochSeconds`. */
export function startOfLocalDay(epochSeconds, timeZone) {
    const key = `${timeZone}|${epochSeconds}`;
    const cached = dayStartCache.get(key);
    if (cached !== undefined)
        return cached;
    const parts = zonedParts(epochSeconds, timeZone);
    const value = zonedEpoch(parts.year, parts.month, parts.day, 0, 0, timeZone);
    if (dayStartCache.size > CACHE_LIMIT)
        dayStartCache.clear();
    dayStartCache.set(key, value);
    return value;
}
/** Epoch seconds of the next local midnight after the one containing `epochSeconds`. */
export function nextLocalDayStart(epochSeconds, timeZone) {
    const key = `${timeZone}|${epochSeconds}`;
    const cached = nextDayCache.get(key);
    if (cached !== undefined)
        return cached;
    const parts = zonedParts(epochSeconds, timeZone);
    const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
    date.setUTCDate(date.getUTCDate() + 1);
    const value = zonedEpoch(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate(), 0, 0, timeZone);
    if (nextDayCache.size > CACHE_LIMIT)
        nextDayCache.clear();
    nextDayCache.set(key, value);
    return value;
}
/** `YYYY-MM-DD` of the local date containing `epochSeconds`. */
export function localDateKey(epochSeconds, timeZone) {
    const parts = zonedParts(epochSeconds, timeZone);
    return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
}
/** Parses `YYYY-MM-DD`; returns `null` for anything else. */
export function parseDateKey(key) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
    if (!match)
        return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31)
        return null;
    return { year, month, day };
}
/** Epoch seconds of local midnight for a parsed date key. */
export function dayStartFromKey(key, timeZone) {
    const parsed = parseDateKey(key);
    if (!parsed)
        return null;
    return zonedEpoch(parsed.year, parsed.month, parsed.day, 0, 0, timeZone);
}
/** Minutes elapsed since local midnight. */
export function minutesSinceLocalMidnight(epochSeconds, timeZone) {
    return (epochSeconds - startOfLocalDay(epochSeconds, timeZone)) / 60;
}
/** `HH:mm` in `timeZone`. */
export function formatClock(epochSeconds, timeZone) {
    const key = `${timeZone}|${epochSeconds}`;
    const cached = clockCache.get(key);
    if (cached !== undefined)
        return cached;
    const parts = zonedParts(epochSeconds, timeZone);
    const value = `${pad2(parts.hour)}:${pad2(parts.minute)}`;
    if (clockCache.size > CACHE_LIMIT)
        clockCache.clear();
    clockCache.set(key, value);
    return value;
}
const dateFormatterCache = new Map();
function dateFormatter(locale, timeZone) {
    const key = `${locale}|${timeZone}`;
    let formatter = dateFormatterCache.get(key);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat(locale, {
            timeZone,
            weekday: "short",
            day: "2-digit",
            month: "short",
        });
        dateFormatterCache.set(key, formatter);
    }
    return formatter;
}
/** e.g. `Tue, 14 Nov`. */
export function formatDayLabel(epochSeconds, timeZone, locale) {
    return dateFormatter(locale, timeZone).format(new Date(epochSeconds * SECOND));
}
/**
 * Hour ticks for one local day. Handles 23/25-hour days: nonexistent hours are
 * skipped, the final tick is pinned to the day end and labelled `24:00`.
 */
export function hourTicks(dayStart, dayEnd, timeZone, stepHours = 1) {
    const cacheKey = `${timeZone}|${dayStart}|${dayEnd}|${stepHours}`;
    const cached = ticksCache.get(cacheKey);
    if (cached)
        return cached;
    const parts = zonedParts(dayStart, timeZone);
    const ticks = [];
    const seen = new Set();
    for (let hour = 0; hour <= 24; hour += stepHours) {
        const epoch = hour === 24 ? dayEnd : zonedEpoch(parts.year, parts.month, parts.day, hour, 0, timeZone);
        const minute = Math.min(Math.max((epoch - dayStart) / 60, 0), (dayEnd - dayStart) / 60);
        const rounded = Math.round(minute * 100) / 100;
        if (seen.has(rounded))
            continue;
        seen.add(rounded);
        ticks.push({
            minute: rounded,
            epoch,
            hour: hour === 24 ? 24 : zonedParts(epoch, timeZone).hour,
            label: hour === 24 ? "24:00" : formatClock(epoch, timeZone),
        });
    }
    if (ticksCache.size > CACHE_LIMIT)
        ticksCache.clear();
    ticksCache.set(cacheKey, ticks);
    return ticks;
}
/** Offset transitions inside a local day (normally zero or one). */
export function dstMarkers(dayStart, dayEnd, timeZone) {
    const cacheKey = `${timeZone}|${dayStart}|${dayEnd}`;
    const cached = dstCache.get(cacheKey);
    if (cached)
        return cached;
    const markers = [];
    const step = 900; // 15 min
    let previousOffset = offsetMsAt(dayStart, timeZone);
    for (let instant = dayStart + step; instant <= dayEnd; instant += step) {
        const offset = offsetMsAt(instant, timeZone);
        if (offset !== previousOffset) {
            let low = instant - step;
            let high = instant;
            while (high - low > 60) {
                const mid = Math.floor((low + high) / 2);
                if (offsetMsAt(mid, timeZone) === previousOffset)
                    low = mid;
                else
                    high = mid;
            }
            const before = Math.round(offsetMsAt(low, timeZone) / 60000);
            const after = Math.round(offsetMsAt(high, timeZone) / 60000);
            markers.push({
                minute: Math.max(0, (high - dayStart) / 60),
                offsetBefore: before,
                offsetAfter: after,
                direction: after > before ? 1 : -1,
            });
            previousOffset = offset;
        }
    }
    if (dstCache.size > CACHE_LIMIT)
        dstCache.clear();
    dstCache.set(cacheKey, markers);
    return markers;
}
/** Formats a duration given in minutes as `4h 35m` / `35m` / `0m`. */
export function formatDuration(minutes) {
    const total = Math.max(0, Math.round(minutes));
    const hours = Math.floor(total / 60);
    const rest = total % 60;
    if (hours > 0 && rest > 0)
        return `${hours}h ${rest}m`;
    if (hours > 0)
        return `${hours}h`;
    return `${rest}m`;
}
/** Compact totals format: `4:35`. */
export function formatDurationCompact(minutes) {
    const total = Math.max(0, Math.round(minutes));
    return `${Math.floor(total / 60)}:${pad2(total % 60)}`;
}
const numberFormatCache = new Map();
export function formatNumber(value, locale) {
    let formatter = numberFormatCache.get(locale);
    if (!formatter) {
        formatter = new Intl.NumberFormat(locale);
        numberFormatCache.set(locale, formatter);
    }
    return formatter.format(value);
}
/** Midpoint of a local day, used for coarse date comparisons. */
export function localNoon(dayStart, timeZone) {
    return dayStart + (nextLocalDayStart(dayStart, timeZone) - dayStart) / 2;
}
export function pad2(value) {
    return value < 10 ? `0${value}` : String(value);
}
